import { defineStore } from "pinia";
import {
    Claim,
    ClaimType,
    CombinedClaimReceiptStatus,
    FamilyMember,
    HsabExpenseByExternalIdRequest,
    HsabExpenseCategory,
    HsabExpenseFormMode,
    HsabExpenseFormState,
    HsabExpenseFormType,
    HsabExpenseOriginator,
    HsabExpenseReimbursementMethod,
    HsabExpenseRequest,
    HsabPayee,
    HsabRecipientType,
    HsabTransactionDetail,
    ManagedExpenseResponse,
    ManagedExpenseType,
    ManuallyMarkedAsPaidStatus,
    ProviderLookupItem,
    SelectorState,
} from "@/shared/types";
import _ from "lodash";
import { useHsabExpenseStore } from "@/employee/store/hsabExpense";
import { useNotifierStore } from "@/shared/store/notifier";
import { useHsabHsaAccountTransactionStore } from "@/employee/store/hsabHsaAccountTransaction";
import { useFamilyStore } from "@/employee/store/family";
import moment from "moment/moment";
import { useHsabAccountsStore } from "@/employee/store/hsabAccounts";
import { Vue } from "@/shared/vuepack";
import { areEquivalent, isNullEmptyOrUndefined } from "@/shared/stringHelper";
import { useContactInfoStore } from "@/shared/store/contactinfo";
import { useHsaDailyBalanceStore } from "@/shared/store/hsaDailyBalance";
import { useFeatureFlagStore } from "@/shared/store/featureFlag";

export const initialState: HsabExpenseFormState = {
    expenseForm: null,
    modalMode: HsabExpenseFormMode.none,
    modalType: HsabExpenseFormType.ExpenseManagement,
    unusedDistribution: null,
    expense: null,
    claim: null,
    checkedShoeboxExpenseEvidence: [],
    currentPayFromOptions: [],
    currentlySelectedCategory: null,
    isTravelCostReimbursementEnabled: false,
    agreedToTerms: false,
    recentlySubmitted: false,
    payFromOption : "",
    selectedPayee: null,
    excludeHsaPlans: true,
    currentPayeeSelectorState: SelectorState.uninitialized,
};

export const useHsabExpenseFormStore = defineStore("hsabExpenseForm", {
    state: (): HsabExpenseFormState => {
        return _.cloneDeep(initialState);
    },
    getters: {
        blankPayee (): HsabPayee {
          return {
            payeeId: null,
            payeeName: "",
            memberAccountNumber: "",
            address1: "",
            address2: "",
            city: "",
            stateCode: "",
            zip: ""
          };
        },
        newExpense (): HsabExpenseRequest {
            const hsabAccountsStore = useHsabAccountsStore();
            return {
                hsabExpenseId: null,
                expenseName: "",
                providerName: "",
                amount: 0,
                comment: "",
                dateOfService: this.defaultDateOfExpense,
                isPayToPayee: false,
                payeeName: "",
                payeeMemberNumber: "",
                payeeAddress1: "",
                payeeAddress2: "",
                payeeCity: "",
                payeeStateCode: "",
                payeeZipCode: "",
                expenseCategory: hsabAccountsStore.userHasActiveNotionalAccounts ? HsabExpenseCategory.Unknown : HsabExpenseCategory.Medical,
                expenseType: "",
                expenseSubType: "",
                expenseSubTypeDisplayName: "",
                externalReferenceId: "",
                numberOfMiles: 0,
                mileageDollarValue: null,
                stagedFilesForUpload: [],
                manuallyMarkedAsPaidStatus: ManuallyMarkedAsPaidStatus.Unknown,
                recipientExternalId: null,
                recipientType: hsabAccountsStore.userHasActiveNotionalAccounts && this.recipients.length > 0 ? HsabRecipientType.None : HsabRecipientType.Subscriber,
                isRecurring: false,
                hsabExpenseEvidenceShoeboxIds: this.getHsabExpenseEvidenceShoeboxIds,
            };
        },
        today () {
            return moment().tz("America/Chicago").format("MM/DD/YYYY");
        },
        hasExpense: state => state.expense !== undefined && state.expense !== null,
        hasClaim: state => state.claim !== undefined && state.claim !== null,
        hasExpenseForm: state => state.expenseForm !== undefined && state.expenseForm !== null,
        hasUnusedDistribution: state => state.unusedDistribution !== undefined && state.unusedDistribution !== null,
        isReadonly () {
            return this.hasExpense && this.expense.isSubmitted;
        },
        showCreateOrEditModal: state => state.modalMode === HsabExpenseFormMode.createOrEdit && state.modalType === HsabExpenseFormType.ExpenseManagement,
        showTransactionItemEditModal: state => state.modalMode === HsabExpenseFormMode.createOrEdit && state.modalType === HsabExpenseFormType.TransactionItem,
        showAdvNavEditModal: state => state.modalMode === HsabExpenseFormMode.createOrEdit && state.modalType === HsabExpenseFormType.AdvNav,
        isExpenseSubmittedToHsa () {
            return this.isExpenseSubmitted &&
                this.expense.reimbursement.isHsaReimbursement;
        },
        isExpenseSubmitted () {
            return this.hasExpense &&
                this.expense.isSubmitted &&
                this.expense.reimbursement;
        },
        defaultDateOfExpense () {
            if (!this.oldestShoeboxReceiptDate) {
                return this.today;
            }
            else return this.oldestShoeboxReceiptDate;
        },
        oldestShoeboxReceiptDate () {
            if (this.hasShoeboxEvidenceAttached) {
                const dateStrings = this.checkedShoeboxExpenseEvidence.map(x => x.dateUploaded);
                const dates = dateStrings.map(dateStr => new Date(dateStr));
                dates.sort((a, b) => a.getTime() - b.getTime());
                const oldestDate = dates[0];
                return moment(oldestDate).format("MM/DD/YYYY");
            }
            else return null;
        },
        hasShoeboxEvidenceAttached () {
            return this.checkedShoeboxExpenseEvidence && this.checkedShoeboxExpenseEvidence.length > 0;
        },
        getHsabExpenseEvidenceShoeboxIds () {
            if (this.hasShoeboxEvidenceAttached) {
                return this.checkedShoeboxExpenseEvidence.map(x => x.hsabExpenseEvidenceShoeboxId);
            }
            else return null;
        },
        isImportedCarrierClaim () {
            return this.hasExpense && this.expense.originator === HsabExpenseOriginator.CarrierClaim;
        },
        isInsuranceClaim () {
            return this.hasExpenseForm && this.expenseForm.originator === HsabExpenseOriginator.InsuranceClaim;
        },
        recipients () {
            const familyStore = useFamilyStore();
            if (familyStore.familyMembers) {
                return _.orderBy(familyStore.familyMembers.filter(x => x.status === "active"), (o: any) => {
                    return moment(o.dateOfBirth, "DD-MM-YYYY");
                }, ["desc"]);
            }
            return [];
        },
        recipientDisplay () {
            if (this.expenseForm !== null && !isNullEmptyOrUndefined(this.expenseForm.recipientExternalId)) {
                const recipient = this.currentRecipient;
                if (recipient) {
                    return recipient.firstName + " " + recipient.lastName;
                }
                return "";
            }

            if (this.hasExpense && this.expense.importedRecipients && this.expense.importedRecipients.length > 0) {
                return this.expense.importedRecipients.map(t => {
                    return t.firstName + " " + t.lastName;
                }).join("\n");
            }
            if (this.unusedDistribution
                && this.unusedDistribution.recipients
                && this.unusedDistribution.recipients.length > 0) {
                return this.unusedDistribution.recipients.map(t => {
                    return t.firstName + " " + t.middleInitial + " " + t.lastName;
                }).join("\n");
            }
            return this.subscriberDisplay;
        },
        subscriberDisplay () {
            const contactInfoStore = useContactInfoStore();
            return contactInfoStore.contactInformation.firstName + " " + contactInfoStore.contactInformation.lastName;
        },
        hasImportedRecipients () {
            return this.hasExpense && this.expense.importedRecipients && this.expense.importedRecipients.length > 0;
        },
        hasValidRecipient (): boolean {
            return (this.hasExpense && this.expense.isValidRecipient)
                || (this.isInsuranceClaim && (this.expenseForm.recipientType === HsabRecipientType.Subscriber
                    || (this.expenseForm.recipientType === HsabRecipientType.Dependant && this.expenseForm.recipientExternalId)
                ));
        },
        currentRecipient () {
            const familyStore = useFamilyStore();
            return familyStore.getFamilyMemberByExternalId(this.expenseForm.recipientExternalId);
        },
        isRecipientRequiredToSubmit () {
            return this.expenseForm.recipientType !== HsabRecipientType.Subscriber;
        },
        isRecipientRequiredToSave () {
            return this.hasExpense &&
                (this.expense.originator === HsabExpenseOriginator.InsuranceClaim
                     || this.expense.originator === HsabExpenseOriginator.CarrierClaim);
        },
        isPayFromRequiredToSubmit (): boolean {
            const featureFlagStore = useFeatureFlagStore();

            return featureFlagStore.flags.bend23571AllowValidPreHsaStartDateExpenses
                && !this.payFromHasHsaOption
                && !this.payFromHasNotionalOption
                && this.expenseForm.expenseCategory !== HsabExpenseCategory.Unknown
                && this.expenseForm.expenseCategory !== undefined
                && this.expenseForm.expenseCategory !== null;
        },
        isValidPreHsaStartDateExpense (): boolean {
            const featureFlagStore = useFeatureFlagStore();

            if (!featureFlagStore.flags.bend23571AllowValidPreHsaStartDateExpenses) {
                return false;
            }

            const payFromIsOnlyHsa = this.payFromHasHsaOption && !this.payFromHasNotionalOption;
            const isNonImportedExpense = isNullEmptyOrUndefined(this.expenseForm.externalExpenseId);
            const isNonCarrierClaim = this.expenseForm.originator !== HsabExpenseOriginator.CarrierClaim;

            if (!payFromIsOnlyHsa || !isNonImportedExpense || !isNonCarrierClaim || !this.HsaEligibleStartDate) {
                return false;
            }

            const hsaEligibleStartDate = this.HsaEligibleStartDate;
            const expenseDate = moment(this.expenseForm.dateOfService);

            const isExpenseDateBeforeHsaStarted = expenseDate.isBefore(hsaEligibleStartDate);

            return payFromIsOnlyHsa
                && isNonImportedExpense
                && isNonCarrierClaim
                && isExpenseDateBeforeHsaStarted;
        },
        payFromHasNotionalOption () {
            return _.some(this.currentPayFromOptions, x => !x.isHsa);
        },
        payFromHasHsaOption () {
            return _.some(this.currentPayFromOptions, x => x.isHsa);
        },
        HsaEligibleStartDate (): moment.Moment | null {
            const payFromOption = this.currentPayFromOptions.find(x => x.isHsa);

            if (payFromOption && payFromOption.planStart) {
                return payFromOption.planStart;
            }

            return null;
        },
        payFromReimbursementMethod (): HsabExpenseReimbursementMethod {
            if (this.currentPayFromOptions.length === 0) {
                return HsabExpenseReimbursementMethod.None;
            } else {
                if (_.every(this.currentPayFromOptions, x => x.reimbursementMethod === HsabExpenseReimbursementMethod.DirectDeposit)) {
                    return HsabExpenseReimbursementMethod.DirectDeposit;
                }
                if (_.every(this.currentPayFromOptions, x => x.reimbursementMethod === HsabExpenseReimbursementMethod.Check)) {
                    return HsabExpenseReimbursementMethod.Check;
                }
            }

            return HsabExpenseReimbursementMethod.None;
        },
        cashAccountBalance (): number {
            const balanceStore = useHsaDailyBalanceStore();
            return balanceStore.instantBalance.amount || balanceStore.balanceOnFileAmount;
        },
        uploadReceiptNotAllowed (): boolean {
            return this.hasExpense
                && this.expense.reimbursement
                && this.expense.reimbursement.denialReasons
                && this.expense.reimbursement.denialReasons.length > 0
                && ! this.expense.reimbursement.denialReasons
                .some(x => x.isUploadReceiptAllowed);
        },
    },
    actions: {
        syncExpenseWithForm () {
            if (this.expense) {
                this.expenseForm = { ...this.newExpense, ...this.expense };
                if (this.expense.numberOfMiles) {
                    this.isTravelCostReimbursementEnabled = true;
                }
            } else if (this.hasUnusedDistribution) {
                this.expenseForm = { ...this.newExpense };
                this.expenseForm.amount = this.unusedDistribution.amount;
                this.expenseForm.providerName = this.unusedDistribution.displayText;
                this.expenseForm.dateOfService = this.unusedDistribution.dateOfService;
                this.expenseForm.expenseCategory = this.unusedDistribution.expenseCategory;
                this.expenseForm.linkedDebitCardTransactionId = this.unusedDistribution.reimbursementId;
                this.expenseForm.isPayToPayee = true;
                this.expenseForm.payeeName = this.unusedDistribution.displayText;
                if (this.unusedDistribution.recipients.length > 0) {
                    this.expenseForm.recipientType = this.unusedDistribution.recipients[0].relationship === "Dependent"
                        ? HsabRecipientType.Dependant
                        : HsabRecipientType.Subscriber;
                }
            } else if (this.hasClaim) {
                this.expenseForm = { ...this.newExpense };
                this.expenseForm.amount = this.claim.memberResponsibility;
                this.expenseForm.providerName = this.claim.providerName;
                this.expenseForm.dateOfService = this.claim.serviceDate;
                this.expenseForm.expenseName = this.claim.serviceName;
                this.expenseForm.comment = this.claim.serviceName;
                this.expenseForm.expenseCategory = this.toExpenseCategory(this.claim.type);
                this.expenseForm.originator = HsabExpenseOriginator.InsuranceClaim;
                this.expenseForm.isPayToPayee = false;
                if (areEquivalent(this.subscriberDisplay, this.claim.patientName)) {
                    this.expenseForm.recipientType = HsabRecipientType.Subscriber;
                }
                this.expenseForm.payeeName = this.claim.providerName;
                this.expenseForm.hashValue = this.claim.hashValue;
                this.expenseForm.externalReferenceId = this.claim.claimNumber;
            } else {
                this.expenseForm = { ...this.newExpense };
                this.expenseForm.manuallyMarkedAsPaidStatus = ManuallyMarkedAsPaidStatus.NotMarkedAsPaid;
                this.isTravelCostReimbursementEnabled = false;
            }
        },
        async loadTalonClaim (claim: Claim) {
            this.claim = claim;
            this.syncExpenseWithForm();
        },
        toExpenseCategory (claimType: ClaimType): HsabExpenseCategory {
            switch (claimType) {
                case ClaimType.Medical:
                    return HsabExpenseCategory.Medical;
                case ClaimType.Pharmacy:
                    return HsabExpenseCategory.Pharmacy;
                case ClaimType.Dental:
                    return HsabExpenseCategory.Dental;
                case ClaimType.Vision:
                    return HsabExpenseCategory.Vision;
                default:
                    return HsabExpenseCategory.Unknown;
            }
        },
        loadRecipients () {
            const familyStore = useFamilyStore();
            const hsabAccountsStore = useHsabAccountsStore();
            familyStore.initialize()
                .then(response => {
                    if (response === undefined || response === null || response.length === 0) {
                        this.updateRecipient(null);
                    }
                    else {
                        if (this.isImportedCarrierClaim) {
                            if (this.hasValidRecipient) {
                                if (this.hasExpense && this.expense.importedRecipients && this.expense.importedRecipients.length > 0 && this.expense.recipientType !== HsabRecipientType.Subscriber) {
                                    const recipient = this.expense.importedRecipients[0];
                                    const found = this.recipients.find( t => t.firstName === recipient.firstName && t.lastName === recipient.lastName &&
                                        (recipient.middleInitial === null || t.middleInitial === recipient.middleInitial));
                                    if (found) {
                                        this.expenseForm.recipientExternalId = found.externalId;
                                        this.expenseForm.recipientType = HsabRecipientType.Dependant;
                                    }
                                }
                            } else {
                                this.expenseForm.recipientExternalId = null;
                                this.expenseForm.recipientType = HsabRecipientType.None;
                            }
                        }
                        else if (this.isInsuranceClaim) {
                            if (this.hasClaim && this.claim.patientName) {
                                if (areEquivalent(this.claim.patientName, this.subscriberDisplay)) {
                                    this.expenseForm.recipientType = HsabRecipientType.Subscriber;
                                    this.expenseForm.recipientExternalId = null;
                                }
                                else {
                                    const found = this.recipients.find(t => areEquivalent(this.claim.patientName, t.fullName));
                                    if (found) {
                                        this.expenseForm.recipientExternalId = found.externalId;
                                        this.expenseForm.recipientType = HsabRecipientType.Dependant;
                                    }
                                }
                            }
                        }
                    }
                });
        },
        updateRecipient (dep: FamilyMember) {
            if (dep) {
                Vue.set(this.expenseForm, "recipientType", HsabRecipientType.Dependant);
                Vue.set(this.expenseForm, "recipientExternalId", dep.externalId);
            } else {
                Vue.set(this.expenseForm, "recipientType", HsabRecipientType.Subscriber);
                Vue.set(this.expenseForm, "recipientExternalId", null);
            }
        },
        updateModalMode (mode: HsabExpenseFormMode) {

            if (this.expenseForm === null && mode === HsabExpenseFormMode.createOrEdit) {
                this.expenseForm = { ...this.newExpense };
            }
            if (mode === HsabExpenseFormMode.none) {
                this.expenseForm = { ...this.newExpense };
                this.expense = null;
                this.claim = null;
                this.unusedDistribution = null;
                this.checkedShoeboxExpenseEvidence = [];
                this.currentPayFromOptions = [];
                this.currentlySelectedCategory = null;
                this.isTravelCostReimbursementEnabled = false;
                this.agreedToTerms = false;
                this.recentlySubmitted = false;
                this.payFromOption = "";
                this.selectedPayee = null;
            } else if (mode === HsabExpenseFormMode.reviewSubmission) {
                this.checkedShoeboxExpenseEvidence = [];
            }
            this.modalMode = mode;
        },
        closeModal () {
            this.modalMode = HsabExpenseFormMode.none;
        },
        setProvideLookupItem (value: ProviderLookupItem) {
            const hsabExpenseStore = useHsabExpenseStore();
            this.expenseForm.providerName = value.providerName;
            this.currentlySelectedCategory = hsabExpenseStore.getCategoriesForUsersPlans.find(t => t.category === value.expenseCategory);
            if (this.currentlySelectedCategory !== null) {
                this.expenseForm.expenseCategory = value.expenseCategory;
                this.expenseForm.expenseType = value.expenseType;
                this.expenseForm.expenseSubType = value.expenseSubType;
                this.expenseForm.expenseSubTypeDisplayName = value.expenseSubTypeDisplayName;
            }
            this.expenseForm.isPayToPayee = value.isPayToPayee;
            if (value.isPayToPayee) {
              if (!this.selectedPayee) {
                this.selectedPayee = this.blankPayee;
              }
              this.selectedPayee.memberAccountNumber = value.accountNumber;
              this.selectedPayee.payeeName = value.providerName;
              this.selectedPayee.address1 = value.address1;
              this.selectedPayee.address2 = value.address2;
              this.selectedPayee.city = value.city;
              this.selectedPayee.stateCode = value.stateCode;
              this.selectedPayee.zip = value.zipCode;
            }
            if (this.selectedPayee !== null) {
                this.currentPayeeSelectorState = SelectorState.Selected;
                this.expenseForm.payeeName = this.selectedPayee.payeeName;
                this.expenseForm.payeeMemberNumber = this.selectedPayee.memberAccountNumber;
                this.expenseForm.payeeAddress1 = this.selectedPayee.address1;
                this.expenseForm.payeeAddress2 = this.selectedPayee.address2;
                this.expenseForm.payeeCity = this.selectedPayee.city;
                this.expenseForm.payeeStateCode = this.selectedPayee.stateCode;
                this.expenseForm.payeeZipCode = this.selectedPayee.zip;
            }
        },
        async loadExpenseForNotionalTransaction (request: HsabExpenseByExternalIdRequest, isPosted: boolean) {
            const hsabExpenseStore = useHsabExpenseStore();
            const notifierStore = useNotifierStore();
            this.expense = await hsabExpenseStore.loadExpenseByExternalId(request);
            if (!this.expense) {
                notifierStore.setDanger("This Expense was not found");
                return;
            }
            this.syncExpenseWithForm();
            if (isPosted) {
                this.modalMode = HsabExpenseFormMode.reviewSubmission;
            } else {
                this.modalType = HsabExpenseFormType.TransactionItem;
                this.modalMode = HsabExpenseFormMode.createOrEdit;
            }
        },
        async loadExpenseForHsaTransaction (transaction: HsabTransactionDetail) {
            const hsabHsaAccountTransactionStore = useHsabHsaAccountTransactionStore();
            const flowIdAssociations = hsabHsaAccountTransactionStore.getFlowIdAssociations(transaction.flowId);
            const hsabExpenseStore = useHsabExpenseStore();
            if (flowIdAssociations.hsabExpenseReimbursementId !== null) {
                // claim already exists, Show details modal
                this.expense = await hsabExpenseStore.loadExpenseById(flowIdAssociations.hsabExpenseId);
                this.modalMode = HsabExpenseFormMode.reviewSubmission;
            } else if (flowIdAssociations.hsabExpenseId !== null) {
                // editing one created from debit card/disbursement, show debit card modal
                this.expense = await hsabExpenseStore.loadExpenseById(flowIdAssociations.hsabExpenseId);
                this.modalType = HsabExpenseFormType.TransactionItem;
                this.modalMode = HsabExpenseFormMode.createOrEdit;
            } else if (flowIdAssociations.reimbursementId !== null) {
                // creating from debit card/disbursement, show debit card modal
                const debitTransReimbursement = await hsabExpenseStore.loadDebitCardReimbursementById(flowIdAssociations.reimbursementId);
                this.unusedDistribution = {
                    managedExpenseType: ManagedExpenseType.Transaction,
                    displayText: debitTransReimbursement.displayText,
                    expenseCategory: debitTransReimbursement.expenseCategory,
                    reimbursementId: flowIdAssociations.reimbursementId,
                    amount: -transaction.amount, // flip the sign do to money out of account showing as negative in ui
                    dateOfService: transaction.paymentDate,
                    planDisplayText: [""],
                    status: [CombinedClaimReceiptStatus.None],
                    flowId: debitTransReimbursement.flowId,
                    recipients: debitTransReimbursement.recipients,
                    expenseId: null,
                    originator: HsabExpenseOriginator.Unknown,
                };
                this.modalMode = HsabExpenseFormMode.debitCard;
            }
            this.syncExpenseWithForm();
        },
        async loadSubmittedExpenseDetails (hsabExpenseId: number) {
            const hsabExpenseStore = useHsabExpenseStore();
            this.recentlySubmitted = true;
            this.expense = await hsabExpenseStore.loadExpenseById(hsabExpenseId, true);
            this.updateModalMode(HsabExpenseFormMode.reviewSubmission);
            this.syncExpenseWithForm();
        },
        async loadExpenseForViewOrEdit (hsabExpenseId: number) {
            const hsabExpenseStore = useHsabExpenseStore();
            this.expense = await hsabExpenseStore.loadExpenseById(hsabExpenseId, true);
            if (this.isExpenseSubmitted) {
                this.updateModalMode(HsabExpenseFormMode.reviewSubmission);
            }
            else {
                this.modalType = HsabExpenseFormType.ExpenseManagement;
                this.updateModalMode(HsabExpenseFormMode.createOrEdit);
            }
            this.syncExpenseWithForm();
        },
        async loadManagedExpenseDetails (managedExpense: ManagedExpenseResponse) {
            const hsabExpenseStore = useHsabExpenseStore();
            let debitTransReimbursement = null;
            switch (managedExpense.managedExpenseType) {
                case ManagedExpenseType.AvailableExpense:
                    this.expense = await hsabExpenseStore.loadExpenseById(managedExpense.expenseId);
                    this.modalType = HsabExpenseFormType.ExpenseManagement;
                    this.updateModalMode(HsabExpenseFormMode.createOrEdit);
                    break;
                case ManagedExpenseType.SubmittedExpense:
                    this.expense = await hsabExpenseStore.loadExpenseById(managedExpense.expenseId);
                    this.updateModalMode(HsabExpenseFormMode.reviewSubmission);
                    break;
                case ManagedExpenseType.Transaction:
                    debitTransReimbursement = await hsabExpenseStore.loadDebitCardReimbursementById(managedExpense.reimbursementId);
                    this.unusedDistribution = {
                        managedExpenseType: ManagedExpenseType.Transaction,
                        displayText: debitTransReimbursement.displayText,
                        expenseCategory: debitTransReimbursement.expenseCategory,
                        reimbursementId: managedExpense.reimbursementId,
                        amount: managedExpense.amount,
                        dateOfService: managedExpense.dateOfService,
                        planDisplayText: [""],
                        status: [CombinedClaimReceiptStatus.None],
                        flowId: debitTransReimbursement.flowId,
                        recipients: debitTransReimbursement.recipients,
                        expenseId: null,
                        originator: HsabExpenseOriginator.Unknown,
                    };
                    this.modalType = HsabExpenseFormType.TransactionItem;
                    this.updateModalMode(HsabExpenseFormMode.debitCard);
                    break;
            }
            this.syncExpenseWithForm();
        },
    }
});